import { auth, db } from "@/lib/firebase";
import { doc, getDoc, updateDoc, arrayRemove, deleteDoc } from "firebase/firestore";
import { signOut as firebaseSignOut, User } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useResetState } from "@/utils/stateReset";

export const AUTH_CHANNEL_NAME = 'auth-events';
type AuthEvent = 'logout' | 'login';

// Create a broadcast channel for auth events
const authChannel = typeof window !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

/**
 * Broadcasts an auth event to other tabs
 */
function broadcastAuthEvent(event: AuthEvent, data?: any) {
  if (authChannel) {
    authChannel.postMessage({ type: event, data });
  }
}

/**
 * Listens for auth events from other tabs
 */
export function onAuthEvent(event: AuthEvent, callback: (data?: any) => void) {
  if (!authChannel) return () => {};
  
  const handler = (e: MessageEvent) => {
    if (e.data?.type === event) {
      callback(e.data.data);
    }
  };
  
  authChannel.addEventListener('message', handler);
  return () => authChannel.removeEventListener('message', handler);
}

interface UserData {
  organisations?: string[];
  mspImpersonation?: boolean;
  mspOrgs?: string[];
  [key: string]: any;
}

/**
 * Clears all site data on logout
 */
async function clearSiteDataOnLogout() {
  localStorage.clear();
  sessionStorage.clear();

  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map(key => caches.delete(key)));
  }

  const dbs = await indexedDB.databases();
  for (const db of dbs) {
    indexedDB.deleteDatabase(db.name);
  }

  navigator.serviceWorker.getRegistrations().then(regs => {
    regs.forEach(r => r.unregister());
  });

  document.cookie.split(";").forEach(cookie => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  });
}

/**
 * Clears MSP impersonation state for a user
 */
async function clearMspImpersonation(user: User): Promise<void> {
  try {
    const usersRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(usersRef);
    
    if (!userDoc.exists()) return;
    
    const userData = userDoc.data() as UserData;
    
    if (userData.organisations?.length > 1 && userData.mspImpersonation) {
      const orgToNotRemove = userData.organisations.find(org =>
        !userData.mspOrgs?.includes(org)
      );
      
      if (orgToNotRemove) {
        const orgToRemove = userData.organisations.find(
          org => org !== orgToNotRemove
        );
        
        if (orgToRemove) {
          await updateDoc(usersRef, {
            organisations: arrayRemove(orgToRemove),
            mspImpersonation: false,
          });
        }
      }
    }
  } catch (error) {
    console.error('Error clearing MSP impersonation:', error);
    throw new Error('Failed to clear MSP impersonation state');
  }
}

/**
 * Signs out the current user
 * @param redirectUrl Optional URL to redirect to after logout (for legacy pages)
 * @returns Promise that resolves when sign out is complete
 */
export async function signOut(redirectUrl?: string): Promise<void> {
  try {
    const user = auth.currentUser;
    
    if (user) {
      try {
        // Clear MSP impersonation if needed
        await clearMspImpersonation(user);
      } catch (error) {
        console.error('Error clearing MSP impersonation:', error);
        // Continue with sign out even if MSP cleanup fails
      }
      // --- SESSION-AWARE LOGOUT ---
      try {
        // Remove session document from Firestore if possible
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId && user.uid) {
          const sessionRef = doc(db, 'users', user.uid, 'sessions', sessionId);
          await deleteDoc(sessionRef);
        }
      } catch (sessionError) {
        console.warn('Failed to delete session document in Firestore during logout:', sessionError);
      }
      // Remove sessionId from localStorage
      localStorage.removeItem('sessionId');
      // Remove session token from sessionStorage
      sessionStorage.removeItem('apiAuthToken');
    }
    
    // Clear ALL cookies accessible from JS
    try {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    } catch (err) {
      console.warn('Error clearing cookies during logout:', err);
    }

    // Clear localStorage and sessionStorage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      console.warn('Error clearing storage during logout:', err);
    }

    // Clear all IndexedDB databases
    try {
      if (window.indexedDB && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        for (const db of dbs) {
          if (db.name) indexedDB.deleteDatabase(db.name);
        }
      }
    } catch (err) {
      console.warn('Error clearing IndexedDB during logout:', err);
    }

    // Clear all CacheStorage caches
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
    } catch (err) {
      console.warn('Error clearing CacheStorage during logout:', err);
    }

    // Clear all site data
    try {
      await clearSiteDataOnLogout();
    } catch (err) {
      console.warn('Error clearing site data during logout:', err);
    }

    // Call server-side logout endpoint to clear httpOnly and domain cookies
    // try {
    //   await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    // } catch (logoutApiError) {
    //   console.warn('Could not reach server-side logout endpoint:', logoutApiError);
    // }
    // Always attempt to sign out from Firebase
    // Broadcast logout event to other tabs
    broadcastAuthEvent('logout');
    
    // Sign out from Firebase
    await firebaseSignOut(auth);
    
    // Log the logout event
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'logout');
    } catch (analyticsError) {
      console.warn('Error logging analytics event:', analyticsError);
    }
  } catch (error) {
    console.error('Sign out error:', error);
    
    console.error('Logout error:', error);
    // Don't throw here - we want to continue even if there's an error
    // The UI will handle showing any error messages to the user
  }
}

/**
 * Signs out of the current tenancy (MSP context)
 * @returns Promise that resolves when sign out is complete
 */
export async function signOutOfTenancy(): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) return Promise.resolve();
    
    await clearMspImpersonation(user);
    return Promise.resolve();
  } catch (error) {
    console.error('Sign out of tenancy error:', error);
    throw new Error('Failed to sign out of tenancy');
  }
}

/**
 * Force sign out without any cleanup (for onboarding or error cases)
 * @param redirectUrl Optional URL to redirect to after force sign out
 */
/**
 * Clears all browser storage and data including IndexedDB, caches, cookies, and service workers
 * @returns Promise that resolves when all storage is cleared
 */
export async function clearAllStorage(): Promise<void> {
  // Clear all IndexedDB databases
  try {
    // Clear firebase storage specifically
    indexedDB.deleteDatabase('firebaseLocalStorageDb');
    
    if (window.indexedDB && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs.map(db => {
          if (db.name) {
            return new Promise<void>((resolve) => {
              const deleteReq = indexedDB.deleteDatabase(db.name!);
              deleteReq.onsuccess = deleteReq.onerror = deleteReq.onblocked = () => resolve();
            });
          }
          return Promise.resolve();
        })
      );
    }
  } catch (e) {
    console.warn('Error clearing IndexedDB databases:', e);
  }

  // Clear all caches
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
  } catch (e) {
    console.warn('Error clearing caches:', e);
  }

  // Clear all cookies
  try {
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch (e) {
    console.warn('Error clearing cookies:', e);
  }

  // Clear service workers
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations();
    if (registrations) {
      await Promise.all(registrations.map(r => r.unregister()));
    }
  } catch (e) {
    console.warn('Error unregistering service workers:', e);
  }

  // Clear localStorage and sessionStorage
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {
    console.warn('Error clearing web storage:', e);
  }
}

/**
 * Force sign out without any cleanup (for onboarding or error cases)
 * @param redirectUrl Optional URL to redirect to after force sign out
 */
export async function forceSignOut(redirectUrl: string = '/login'): Promise<void> {
  try {
    // Clear ALL cookies accessible from JS
    try {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    } catch (err) {
      console.warn('Error clearing cookies during force sign out:', err);
    }

    // Clear localStorage and sessionStorage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      console.warn('Error clearing storage during force sign out:', err);
    }

    // Clear all IndexedDB databases
    try {
      if (window.indexedDB && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        for (const db of dbs) {
          if (db.name) indexedDB.deleteDatabase(db.name);
        }
      }
    } catch (err) {
      console.warn('Error clearing IndexedDB during force sign out:', err);
    }

    // Clear all CacheStorage caches
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
    } catch (err) {
      console.warn('Error clearing CacheStorage during force sign out:', err);
    }

    // Broadcast logout event to other tabs
    broadcastAuthEvent('logout');
    
    // Sign out from Firebase
    await firebaseSignOut(auth);

    // // Call server-side logout endpoint to clear httpOnly and domain cookies
    // try {
    //   await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    // } catch (logoutApiError) {
    //   console.warn('Could not reach server-side logout endpoint:', logoutApiError);
    // }

    window.location.assign(redirectUrl);
  } catch (error) {
    console.error('Force sign out error:', error);
    window.location.assign(redirectUrl);
  }
}
